import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/apiService';
import { useTranslation } from '../context/LanguageContext';

/**
 * 📜 AVV-Gate — blockiert die App, bis der Betreiber den Auftragsverarbeitungs-
 * vertrag (Art. 28 DSGVO) einmalig bestätigt hat.
 *
 * Bewusst als eigenständige, selbstprüfende Komponente: Sie hört auf das
 * `kiosk:auth-changed`-Event (nach Login/Session-Wiederherstellung), fragt
 * `/avv/status` ab und legt sich als Vollbild-Overlay über ALLES, wenn die
 * aktuelle Fassung noch nicht akzeptiert wurde. Kein Eingriff in die bestehende
 * App-Logik — nur ein Wächter davor.
 *
 * Muss echte Zustimmung sein (nicht nur ein Hinweis), sonst ist sie nach
 * Art. 28 nicht nachweisbar. Deshalb: modal, sperrend, mit Häkchen + Button.
 */
export default function AvvGate() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null); // { currentVersion, accepted } | null
  const [user, setUser] = useState(null);
  const [gelesen, setGelesen] = useState(false);
  const [sende, setSende] = useState(false);
  const [fehler, setFehler] = useState('');

  const pruefe = useCallback(async () => {
    try {
      const s = await api.get('/avv/status');
      setStatus(s);
    } catch {
      // Kein Netz / nicht angemeldet → Gate nicht zeigen, App läuft normal weiter.
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    // Nach Login/Session-Wiederherstellung prüfen
    const onAuth = (e) => {
      const u = e.detail && e.detail.user;
      setUser(u || null);
      if (u) pruefe();
      else setStatus(null);
    };
    window.addEventListener('kiosk:auth-changed', onAuth);

    // Falls die Session schon steht, bevor diese Komponente montiert wurde:
    try {
      const gespeichert = JSON.parse(localStorage.getItem('kiosk_user_data') || 'null');
      if (gespeichert) { setUser(gespeichert); pruefe(); }
    } catch { /* egal */ }

    return () => window.removeEventListener('kiosk:auth-changed', onAuth);
  }, [pruefe]);

  const akzeptieren = async () => {
    if (!status) return;
    setSende(true);
    setFehler('');
    try {
      await api.post('/avv/accept', { version: status.currentVersion });
      setStatus({ ...status, accepted: true });
    } catch (err) {
      setFehler(err?.message || t('ui.avv.confirmFailed'));
    } finally {
      setSende(false);
    }
  };

  // Nur anzeigen, wenn angemeldet UND aktuelle Fassung noch nicht akzeptiert
  if (!user || !status || status.accepted) return null;

  return (
    <div style={ov.backdrop} role="dialog" aria-modal="true" aria-label={t('ui.avv.title')}>
      <div style={ov.card}>
        <div style={ov.icon}>📜</div>
        <h2 style={ov.h2}>{t('ui.avv.title')}</h2>
        <p style={ov.lead}>
          {t('ui.avv.lead')}
        </p>

        <div style={ov.summary}>
          <ul style={ov.ul}>
            <li>{t('ui.avv.hosting')}</li>
            <li>{t('ui.avv.deletion')}</li>
            <li>{t('ui.avv.aiProcessing')}</li>
            <li>{t('ui.avv.subprocessors')}</li>
          </ul>
          <a href="/avv.html" target="_blank" rel="noopener noreferrer" style={ov.link}>
            📄 {t('ui.avv.readFull', { version: status.currentVersion })}
          </a>
        </div>

        <label style={ov.check}>
          <input
            type="checkbox"
            checked={gelesen}
            onChange={(e) => setGelesen(e.target.checked)}
            style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0 }}
          />
          <span>
            {t('ui.avv.checkbox')}
          </span>
        </label>

        {fehler && <p style={ov.error}>{fehler}</p>}

        <button
          onClick={akzeptieren}
          disabled={!gelesen || sende}
          style={{ ...ov.button, opacity: (!gelesen || sende) ? 0.5 : 1,
                   cursor: (!gelesen || sende) ? 'default' : 'pointer' }}
        >
          {sende ? t('ui.avv.saving') : t('ui.avv.confirmButton')}
        </button>

        <p style={ov.small}>
          {t('ui.avv.logged')}
        </p>
      </div>
    </div>
  );
}

const ov = {
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 100000,
    background: 'rgba(15,20,28,0.72)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16, overflowY: 'auto',
  },
  card: {
    background: '#fff', color: '#1f2933', borderRadius: 14,
    maxWidth: 560, width: '100%', padding: '28px 26px',
    boxShadow: '0 16px 50px rgba(0,0,0,0.35)', margin: 'auto',
  },
  icon: { fontSize: 40, textAlign: 'center' },
  h2: { margin: '6px 0 14px', textAlign: 'center', fontSize: 22 },
  lead: { fontSize: 14.5, lineHeight: 1.55, marginBottom: 16 },
  summary: {
    background: '#f6f9fc', border: '1px solid #dbe6f1',
    borderRadius: 10, padding: '14px 16px', marginBottom: 16,
  },
  ul: { margin: '0 0 10px 18px', fontSize: 13.5, lineHeight: 1.5 },
  link: { color: '#1976d2', fontWeight: 600, fontSize: 14, textDecoration: 'none' },
  check: {
    display: 'flex', gap: 10, alignItems: 'flex-start',
    fontSize: 14, lineHeight: 1.5, margin: '4px 0 16px', cursor: 'pointer',
  },
  error: { color: '#d32f2f', fontSize: 13.5, marginBottom: 10 },
  button: {
    width: '100%', padding: '14px', fontSize: 16, fontWeight: 700,
    background: '#1976d2', color: '#fff', border: 'none', borderRadius: 10,
  },
  small: { fontSize: 11.5, color: '#6b7885', textAlign: 'center', marginTop: 12 },
};
